import React, { useEffect, useRef, useState } from 'react';

import styles from './css/CustomScrollbar.module.css';

const CustomScrollbar = ({ containerRef, listRef }) => {
  const [scrollPercentage, setScrollPercentage] = useState(0);
  const [trackHeight, setTrackHeight] = useState(0);
  const [thumbHeight, setThumbHeight] = useState(27);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(0);
  const [dragStartScrollTop, setDragStartScrollTop] = useState(0);
  const trackRef = useRef(null);

  useEffect(() => {
    const container = listRef?.current;
    const containerElement = containerRef?.current;
    if (!container) {
      return undefined;
    }

    const updateScrollbar = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const maxScroll = scrollHeight - clientHeight;
      const percentage = maxScroll > 0 ? (scrollTop / maxScroll) * 100 : 0;
      // Use offsetHeight to include borders, matching scrollbar track height
      const trackHeightValue = container.offsetHeight;

      // Calculate thumb height dynamically
      const visibleRatio = scrollHeight > 0 ? clientHeight / scrollHeight : 1;
      let calculatedThumbHeight = trackHeightValue * visibleRatio;

      // Minimum thumb size from figma 27px
      calculatedThumbHeight = Math.max(
        27,
        Math.min(calculatedThumbHeight, trackHeightValue - 8),
      );

      setScrollPercentage(Math.min(100, Math.max(0, percentage)));
      setTrackHeight(trackHeightValue);
      setThumbHeight(calculatedThumbHeight);
    };

    updateScrollbar();
    container.addEventListener('scroll', updateScrollbar);
    window.addEventListener('resize', updateScrollbar);

    // Use ResizeObserver to detect when container or list size changes
    const resizeObservers = [];
    if (window.ResizeObserver) {
      // Observe container element for height changes
      if (containerElement) {
        const containerObserver = new ResizeObserver(() => {
          updateScrollbar();
        });
        containerObserver.observe(containerElement);
        resizeObservers.push(containerObserver);
      }

      // Observe list container for content size changes
      const listObserver = new ResizeObserver(() => {
        updateScrollbar();
      });
      listObserver.observe(container);
      resizeObservers.push(listObserver);
    }

    const cleanup = () => {
      container.removeEventListener('scroll', updateScrollbar);
      window.removeEventListener('resize', updateScrollbar);
      resizeObservers.forEach((observer) => {
        observer.disconnect();
      });
    };

    return cleanup;
  }, [listRef, containerRef]);

  const handleTrackClick = (e) => {
    const container = listRef?.current;
    const track = trackRef.current;
    if (!container || !track) return;

    const rect = track.getBoundingClientRect();
    const clickY = e.clientY - rect.top;
    const currentTrackHeight = rect.height;
    const percentage = clickY / currentTrackHeight;

    const { scrollHeight, clientHeight } = container;
    const maxScroll = scrollHeight - clientHeight;
    container.scrollTop = percentage * maxScroll;
  };

  const handleThumbMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    setDragStart(e.clientY);
    setDragStartScrollTop(listRef?.current?.scrollTop || 0);
  };

  useEffect(() => {
    if (!isDragging) {
      return undefined;
    }

    const handleMouseMove = (e) => {
      const container = listRef?.current;
      const track = trackRef.current;
      if (!container || !track) return;

      const rect = track.getBoundingClientRect();
      const currentTrackHeight = rect.height;
      const deltaY = e.clientY - dragStart;
      const deltaPercentage = (deltaY / currentTrackHeight) * 100;

      const { scrollHeight, clientHeight } = container;
      const maxScroll = scrollHeight - clientHeight;
      const scrollDelta = (deltaPercentage / 100) * maxScroll;

      container.scrollTop = Math.min(
        maxScroll,
        Math.max(0, dragStartScrollTop + scrollDelta),
      );
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart, dragStartScrollTop, listRef]);

  const container = listRef?.current;
  if (!container) {
    return null;
  }

  const handleTrackKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const scrollContainer = listRef?.current;
      if (!scrollContainer) return;

      const { scrollHeight, clientHeight } = scrollContainer;
      const maxScroll = scrollHeight - clientHeight;
      scrollContainer.scrollTop = Math.min(
        maxScroll,
        scrollContainer.scrollTop + clientHeight,
      );
    }
  };

  return (
    <div
      ref={trackRef}
      aria-controls="dropdown-list"
      aria-valuenow={scrollPercentage}
      className={styles.scrollbarTrack}
      onClick={handleTrackClick}
      onKeyDown={handleTrackKeyDown}
      role="scrollbar"
      style={{
        height: trackHeight > 0 ? `${trackHeight}px` : 'calc(100% - 8px)',
      }}
      tabIndex={0}
    >
      <div
        className={styles.scrollbarThumb}
        onMouseDown={handleThumbMouseDown}
        role="button"
        style={{
          height: `${thumbHeight}px`,
          top: trackHeight > 0 && thumbHeight > 0
            ? `${(scrollPercentage / 100) * (trackHeight - thumbHeight)}px`
            : `${scrollPercentage}%`,
        }}
        tabIndex={0}
      />
    </div>
  );
};

export { CustomScrollbar };
